import { useEffect, useState } from 'react';
import { AppShell } from '../../components/app/AppShell';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Dot } from '../../components/ui/Dot';
import { InputField } from '../../components/ui/InputField';
import { Modal } from '../../components/ui/Modal';
import { StatCard } from '../../components/ui/StatCard';
import { api, ApiError } from '../../lib/api';
import { tasksApi } from '../../lib/tasksApi';
import { isReadOnly } from '../../lib/tokens';
import type { Me, Notification, Task, TaskPriority, TaskStats, TaskStatus } from '../../lib/types';

const navItems = [
  { label: 'Главная', to: '/app' },
  { label: 'Мои задачи', to: '/app/tasks' },
  { label: 'Уведомления', to: '/app/tasks' },
  { label: 'Календарь' },
  { label: 'Команда' },
  { label: 'Настройки', to: '/app/profile' },
];

const priorityTone: Record<TaskPriority, 'danger' | 'warning' | 'neutral'> = {
  critical: 'danger',
  high: 'danger',
  normal: 'warning',
  low: 'neutral',
};

const priorityLabel: Record<TaskPriority, string> = {
  critical: 'Критичный',
  high: 'Высокий',
  normal: 'Средний',
  low: 'Низкий',
};

const statusLabel: Record<TaskStatus, string> = {
  new: 'Новая',
  in_progress: 'В работе',
  review: 'На проверке',
  done: 'Готово',
  cancelled: 'Отменена',
};

function formatDue(task: Task): { text: string; overdue: boolean } {
  if (!task.due_at) return { text: '—', overdue: false };
  const due = new Date(task.due_at);
  const overdue = due.getTime() < Date.now() && task.status !== 'done' && task.status !== 'cancelled';
  const text = due.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
  return { text: overdue ? `Просрочено · ${text}` : text, overdue };
}

export function TasksPage() {
  const [me, setMe] = useState<Me | null>(null);
  const [stats, setStats] = useState<TaskStats | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const loadTasks = async () => {
    try {
      const list = await tasksApi.listTasks({
        search: search || undefined,
        status: statusFilter || undefined,
        priority: priorityFilter || undefined,
      });
      setTasks(list);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось загрузить задачи.');
    }
  };

  const loadStats = async () => {
    try {
      setStats(await tasksApi.taskStats());
    } catch {
      // KPI необязательны для работы страницы
    }
  };

  const loadNotifications = async () => {
    try {
      setNotifications(await tasksApi.listNotifications());
    } catch {
      // список уведомлений необязателен для работы страницы
    }
  };

  useEffect(() => {
    api.getMe().then(setMe).catch(() => {});
    loadStats();
    loadNotifications();
  }, []);

  useEffect(() => {
    loadTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, priorityFilter]);

  const handleStatusChange = async (task: Task, status: TaskStatus) => {
    await tasksApi.updateTask(task.id, { status });
    loadTasks();
    loadStats();
  };

  const handleNotificationClick = async (n: Notification) => {
    if (n.read_at) return;
    await tasksApi.markNotificationRead(n.id);
    loadNotifications();
  };

  const unreadCount = notifications.filter((n) => !n.read_at).length;

  return (
    <AppShell
      orgLabel={me?.workspace?.name ?? me?.workspace?.organization_name ?? '—'}
      navItems={navItems}
      backLabel="На карту"
      backTo="/app"
      title="Задачи и уведомления"
      breadcrumb="Личный кабинет / Мои задачи"
      userInitials={me ? `${me.first_name?.[0] ?? ''}${me.last_name?.[0] ?? ''}`.toUpperCase() || '??' : '…'}
      headerRight={
        isReadOnly() ? undefined : (
          <Button type="button" onClick={() => setCreateOpen(true)}>
            Новая задача
          </Button>
        )
      }
    >
      <div className="mx-auto flex max-w-[1400px] flex-col gap-6">
        {error && <p className="text-xs font-medium text-status-warning">{error}</p>}

        <div className="flex gap-4">
          <StatCard label="К выполнению" value={stats?.todo ?? '—'} tone="primary" icon="●" />
          <StatCard label="Просрочено" value={stats?.overdue ?? '—'} tone="danger" icon="!" />
          <StatCard label="На проверке" value={stats?.review ?? '—'} tone="warning" icon="●" />
          <StatCard label="Завершено за неделю" value={stats?.completed_this_week ?? '—'} tone="success" icon="✓" />
        </div>

        <div className="grid grid-cols-[1fr_320px] items-start gap-6">
          <Card
            title="Мои задачи"
            action={<span className="text-[11px] font-medium text-action-primary">{tasks.length} активных</span>}
          >
            <div className="flex items-center gap-3 rounded-[10px] bg-bg-surface-secondary px-4 py-2 text-[11px]">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="⌕ Поиск по задачам"
                className="flex-1 bg-transparent text-text-primary outline-none placeholder:text-text-secondary"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-transparent font-medium text-text-primary outline-none"
              >
                <option value="">Статус: Все</option>
                {Object.entries(statusLabel).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="bg-transparent font-medium text-text-primary outline-none"
              >
                <option value="">Приоритет: Все</option>
                {Object.entries(priorityLabel).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-3 flex flex-col gap-1">
              {tasks.map((task, i) => {
                const due = formatDue(task);
                return (
                  <div
                    key={task.id}
                    className={`grid grid-cols-[12px_1fr_130px_100px_84px_110px] items-center gap-4 rounded-[10px] px-3 py-3 ${
                      i % 2 === 0 ? 'bg-bg-surface' : 'bg-bg-canvas'
                    }`}
                  >
                    <Dot tone={priorityTone[task.priority]} size={9} />
                    <div className="min-w-0">
                      <p className="truncate text-xs font-semibold text-text-primary">{task.title}</p>
                      {task.description && (
                        <p className="mt-1 truncate text-[10px] text-text-secondary">{task.description}</p>
                      )}
                    </div>
                    <span className={`text-[10px] font-medium ${due.overdue ? 'text-status-danger' : 'text-text-secondary'}`}>
                      {due.text}
                    </span>
                    <span className="text-[10px] font-medium text-text-primary">{priorityLabel[task.priority]}</span>
                    <span className="truncate text-[10px] font-medium text-text-primary">
                      {task.assignee_name ?? '—'}
                    </span>
                    {isReadOnly() ? (
                      <span className="text-[10px] font-medium text-text-primary">{statusLabel[task.status]}</span>
                    ) : (
                      <select
                        value={task.status}
                        onChange={(e) => handleStatusChange(task, e.target.value as TaskStatus)}
                        className="rounded-md border border-border-default bg-bg-surface px-1.5 py-1 text-[10px] text-text-primary outline-none"
                      >
                        {Object.entries(statusLabel).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                );
              })}
              {tasks.length === 0 && (
                <p className="py-6 text-center text-xs text-text-secondary">Задач не найдено.</p>
              )}
            </div>
          </Card>

          <Card
            title="Уведомления"
            action={
              <span className="rounded-full bg-bg-surface-secondary px-3 py-1 text-[9px] font-semibold text-action-primary">
                {unreadCount} новых
              </span>
            }
          >
            <div className="flex flex-col gap-2">
              {notifications.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => handleNotificationClick(n)}
                  className={`rounded-[10px] p-3 text-left ${!n.read_at ? 'bg-bg-surface-secondary' : 'bg-bg-surface'}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Dot tone={n.read_at ? 'neutral' : 'primary'} size={8} />
                      <p className="text-[11px] font-semibold text-text-primary">{n.title}</p>
                    </div>
                    <span className="shrink-0 text-[9px] font-medium text-text-secondary">
                      {new Date(n.created_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}
                    </span>
                  </div>
                  {n.body && <p className="mt-1.5 pl-4 text-[10px] text-text-secondary">{n.body}</p>}
                </button>
              ))}
              {notifications.length === 0 && (
                <p className="py-6 text-center text-xs text-text-secondary">Уведомлений нет.</p>
              )}
            </div>
          </Card>
        </div>
      </div>

      <CreateTaskModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false);
          loadTasks();
          loadStats();
        }}
      />
    </AppShell>
  );
}

function CreateTaskModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('normal');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle('');
      setDescription('');
      setPriority('normal');
      setError(null);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await tasksApi.createTask({ title, description: description || undefined, priority });
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось создать задачу.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <h2 className="text-lg font-semibold text-text-primary">Новая задача</h2>
        <InputField label="Название" value={title} onChange={(e) => setTitle(e.target.value)} required />
        <InputField
          label="Описание"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <div className="flex flex-col gap-2">
          <label className="text-xs font-medium text-text-primary">Приоритет</label>
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value as TaskPriority)}
            className="h-10 rounded-md border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none"
          >
            {Object.entries(priorityLabel).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
        {error && <p className="text-xs font-medium text-status-warning">{error}</p>}
        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? 'Создаём…' : 'Создать задачу'}
        </Button>
      </form>
    </Modal>
  );
}
